<template>
    <checkout-processing></checkout-processing>
</template>

<script>
import { iosview } from '../iosview';
import axios from 'axios';
import CheckoutProcessing from '../checkout/components/loaders/CheckoutProcessing.vue';
import Swal from 'sweetalert2';

export default {
    name: 'IosPwaViewListeners',
    components: {
        CheckoutProcessing,
    },
    data() {
        return {
            upsellUrlEventTarget: new EventTarget(),
        };
    },
    computed: {
        backToCoursesUrl() {
            switch (window.App.appEnvironment) {
                case 'production':
                    return 'https://courses.iphonephotographyschool.com';
                case 'local':
                    return 'http://courses.ips.test/';
                default:
                    return `https://${window.App.appEnvironment}-courses.iphonephotographyschool.com`;
            }
        },
    },
    methods: {
        // Handlers
        handleIapPurchaseResult(event) {
            if (event && event.detail) {
                this.verifyReceipt(event);
            }
        },
        handleIapPurchaseFailure(event) {
            this.$store.commit('setProcessingCheckout', false);
            if (event && event.detail) {
                const detailObject = JSON.parse(event.detail);
                let customMessage = '';
                let customTitle = '';
                switch (detailObject.error) {
                    case 'Cannot Make Payments':
                        customTitle = this.$t('ios_error.cantMakePayments.title');
                        customMessage = this.$t('ios_error.cantMakePayments.message');
                        break;
                    case 'Privacy Acknowledgement Required':
                        customTitle = this.$t('ios_error.privacyAcknowledgement.title');
                        customMessage = this.$t('ios_error.privacyAcknowledgement.message');
                        break;
                    case 'Payment Invalid':
                        customTitle = this.$t('ios_error.paymentInvalid.title');
                        customMessage = this.$t('ios_error.paymentInvalid.message');
                        break;
                    case 'Cancelled':
                        break;
                    default:
                        customTitle = this.$t('ios_error.unexpectedError.title');
                        customMessage = this.$t('ios_error.unexpectedError.message');
                        break;
                }

                if (detailObject.error !== 'Cancelled') {
                    console.error('Error:', detailObject);
                    Swal.fire({
                        title: customTitle,
                        text: customMessage,
                    });
                }
            }
        },

        // Helpers
        detectPurchaseFormat(detailObject) {
            if (detailObject.transactionId && detailObject.jwsRepresentation) {
                return 'storekit2';
            } else if (detailObject.receipt) {
                return 'legacy';
            }
            return 'unknown';
        },
        async verifyReceipt(event) {
            // Backward compatibility: Call finalized method (auto-detects StoreKit 1 vs 2)
            iosview.pwaInAppPurchaseFinalized();

            const detailObject = JSON.parse(event.detail);
            const format = this.detectPurchaseFormat(detailObject);

            const receiptData = detailObject.receipt || '';
            const price = detailObject.price || 0;
            const currencyCode = detailObject.currencyCode || 'USD';
            const countryCode = detailObject.countryCode || 'US';

            let apiEndpoint, requestData;

            if (format === 'storekit2') {
                apiEndpoint = '/api/apple/purchase/verify';
                requestData = {
                    jws: receiptData,
                    product_id: detailObject.productId,
                    currency: currencyCode,
                    price: price,
                    country_code: countryCode,
                    uid: window.App.contact.ips_id,
                    psh: window.App.sessionId,
                };
            } else {
                // Legacy StoreKit 1 API (existing)
                apiEndpoint = '/api/apple/verify-receipt';
                requestData = {
                    receipt_data: receiptData,
                    country_code: countryCode,
                    currency: currencyCode,
                    price: price,
                    original_url: window.App.originalUrl,
                    uid: window.App.contact.ips_id,
                    psh: window.App.sessionId,
                };
            }

            try {
                const response = await axios.post(apiEndpoint, requestData, {
                    headers: {
                        'X-Requested-With': 'mobile',
                        Accept: 'application/json',
                    },
                });
                this.$store.commit('setProcessingCheckout', false);

                const receiptResponse = response.data.data;

                if (receiptResponse.upsell_app_product_id) {
                    // In case of lifetime updates purchase upsell url is already generated.
                    if (!window.App.ios_upsell_url) {
                        iosview.parseIosPwaProductData(receiptResponse);
                    } else {
                        window.location.href = window.App.ios_upsell_url;
                    }
                } else {
                    window.location.href = receiptResponse.upsell_url;
                }

                if (receiptResponse.lifetime_updates) {
                    this.$store.commit('setLifetimeUpdates', {
                        standalone: true,
                    });
                    this.$store.commit('openCheckout', {
                        step: 3,
                    });
                } else {
                    this.upsellUrlEventTarget.addEventListener('generatedUpsellUrl', () => {
                        window.location.href = window.App.ios_upsell_url;
                    });
                }
            } catch (error) {
                this.$store.commit('setProcessingCheckout', false);
                window.ipse.dispatch({
                    action: 'purchase_failed',
                    data_event: {
                        message: error.response?.data || 'Unknown error',
                    },
                });
                // Check if the error has a response and data property
                if (error.response && error.response.data && error.response.data.message) {
                    Swal.fire({
                        text: error.response.data.message,
                    }).then(() => {
                        window.location.href = this.backToCoursesUrl;
                    });
                } else {
                    // Fallback error message if the expected data is not present
                    Swal.fire({
                        title: this.$t('ios_error.unexpectedError.title'),
                        text: this.$t('ios_error.unexpectedError.message'),
                    });
                }
            }
        },
        generateUpsellUrl(upsellProducts) {
            const originalPriceMultiplier = window.App.ios_upsell_price_coefficient
                ? window.App.ios_upsell_price_coefficient
                : 5;
            let luPrice = null;
            let luLocale = null;
            let productPrice = null;
            let productLocale = null;
            const iosSalesUrlParameters = 'iosview&iospwa';
            upsellProducts.forEach((product) => {
                if (product.productIdentifier.includes('lifetime_updates')) {
                    luPrice = product.price || 0;
                    luLocale = product.priceLocale;
                } else {
                    productPrice = product.price || 0;
                    productLocale = product.priceLocale;
                }
            });
            const oldPrice = Math.ceil((productPrice * originalPriceMultiplier) / 5) * 5;

            const upsellUrl =
                window.App.ios_temp_upsell_url +
                `&${iosSalesUrlParameters}` +
                (luPrice ? `&luPrice=${luLocale}${luPrice}` : '') +
                `&oldPrice=${productLocale}${oldPrice}&newPrice=${productLocale}${productPrice}`;

            window.App.ios_temp_upsell_url = '';
            window.App.ios_upsell_url = upsellUrl;

            const event = new CustomEvent('generatedUpsellUrl');
            this.upsellUrlEventTarget.dispatchEvent(event);

            if (window.App && window.App.no_thanks_clicked === true) {
                window.location.href = window.App.ios_upsell_url;
            }
        },
        handleIapSomeProductsResult(event) {
            if (event && event.detail) {
                const detailObject = JSON.parse(event.detail);
                if (!detailObject || detailObject.length === 0) {
                    throw new Error('event.detail is an empty array.');
                }
                this.generateUpsellUrl(detailObject);
            }
        },
        handleIapSomeProductsResultFailure(event) {
            if (event && event.detail) {
                console.error('iap-some-products-result-failure:', event.detail);
            }
        },
    },
    mounted() {
        window.addEventListener('iap-purchase-result', this.handleIapPurchaseResult);
        window.addEventListener('iap-purchase-result-failure', this.handleIapPurchaseFailure);
        window.addEventListener('iap-some-products-result', this.handleIapSomeProductsResult);
        window.addEventListener('iap-some-products-result-failure', this.handleIapSomeProductsResultFailure);
    },
    beforeUnmount() {
        window.removeEventListener('iap-purchase-result', this.handleIapPurchaseResult);
        window.removeEventListener('iap-purchase-result-failure', this.handleIapPurchaseFailure);
        window.removeEventListener('iap-some-products-result', this.handleIapSomeProductsResult);
        window.removeEventListener('iap-some-products-result-failure', this.handleIapSomeProductsResultFailure);
    },
};
</script>
